import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  Divider,
  Collapse,
} from "@mui/material";
import {
  FaChartBar,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaUserTie,
  FaFileInvoice,
  FaWallet,
  FaBullhorn,
  FaSignOutAlt,
  FaBook,
  FaTasks,
  FaBoxOpen,
  FaLayerGroup,
  FaListAlt,
  FaUniversity,
  FaChevronDown,
  FaChevronRight,
  FaUsers,
  FaClipboardList,
  FaRegFileAlt,
  FaMoneyBillWave,
  Fa500Px,
  FaCog,
  FaBell,
  FaBookOpen,
  FaExchangeAlt,
} from "react-icons/fa";
import { MdAccountBalance } from "react-icons/md";

import { MdClass } from "react-icons/md";
import { GiNotebook } from "react-icons/gi";
import { TbReportSearch } from "react-icons/tb";

const DRAWER_WIDTH = 260;
const MINI_WIDTH = 0;

export default function Sidebar({ open = true, setOpen = () => { } }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:900px)");

  const [openMenu, setOpenMenu] = useState("");
  const role = localStorage.getItem("role");
  const roleIsSuper = role === "super_admin";

  // Role-specific routes
  const billingRoute = roleIsSuper ? "/admin/all-billing" : "/admin/student-billing";
  const walletRoute = roleIsSuper ? "/admin/wallet" : "/admin/staff/wallet";

  // Dropdown sections — organized by priority / usefulness
  const dropdownSections = useMemo(
    () => [
      {
        title: "Students",
        icon: <FaUserGraduate />,
        items: [
          { label: "Add Student", icon: <FaUserGraduate />, to: "/admin/add-student" },
          { label: "View Students", icon: <FaListAlt />, to: "/admin/view-student" },
          { label: "Student Promotion", icon: <FaListAlt />, to: "/admin/student/promotion" },
          { label: roleIsSuper ? "All Billing" : "Student Billing", icon: <FaFileInvoice />, to: billingRoute },
          { label: "Product Billing", icon: <FaListAlt />, to: "/admin/student/product-billing" },
          { label: "Product Billing History", icon: <FaListAlt />, to: "/admin/student/product-billing/history" },
          { label: "Student ID Card", icon: <FaListAlt />, to: "/admin/student/id-card" },
        ],
      },
      {
        title: "Exams & Results",
        icon: <GiNotebook />,
        items: [
          { label: "Add Exam", icon: <GiNotebook />, to: "/admin/exam-management/add" },
          { label: "Exam Subject Assign", icon: <GiNotebook />, to: "/admin/exam-subject-management/add" },
          { label: "Mark Roll Entry", icon: <GiNotebook />, to: "/admin/mark-roll/entry" },
          { label: "Mark Sheet List", icon: <GiNotebook />, to: "/admin/mark-sheet/list" },
          { label: "Admit Card", icon: <GiNotebook />, to: "/admin/admit-card/entry" },
          // { label: "Admit List", icon: <GiNotebook />, to: "/admin/admit-card/list" },
          { label: "Mark Types", icon: <GiNotebook />, to: "/admin/mark-types/list" },
          { label: "Grade Rules", icon: <GiNotebook />, to: "/admin/grade-rules/list" },
          // { label: "Template Builder", icon: <GiNotebook />, to: "/admin/exam/templates/builder" },

        ],
      },
      {
        title: "Fees & Accounts",
        icon: <FaMoneyBillWave />,
        items: [
          { label: "Student Fees Structure", icon: <FaMoneyBillWave />, to: "/admin/fees_structure/list" },
          { label: "Other Fees Structure", icon: <FaTasks />, to: "/admin/fees_structure/others" },
          { label: "Billing / Invoices", icon: <FaFileInvoice />, to: billingRoute },
          { label: roleIsSuper ? "Admin Wallet" : "Staff Wallet", icon: <FaWallet />, to: walletRoute },
          { label: "Wallet History", icon: <FaClipboardList />, to: "/admin/wallet-history" },
          { label: "Unpaid Students", icon: <FaClipboardList />, to: "/admin/unpaid-students" },
        ],
      },
      {
        title: "Teachers & Staff",
        icon: <FaChalkboardTeacher />,
        items: [
          {
            label: "Add Teacher",
            icon: <FaChalkboardTeacher />,
            to: "/admin/teacher-add"
          },
          {
            label: "Assign Subject to Teacher",
            icon: <FaUserTie />,
            to: "/admin/teacher-subject/assign"
          },
          {
            label: "Assign Class Teacher",
            icon: <FaChalkboardTeacher />,
            to: "/admin/class-teacher/assign"
          },
          {
            label: "List Permission Master",
            icon: <FaUserTie />,
            to: "/admin/permission-master/list"
          },
          {
            label: "List Teacher Permission",
            icon: <FaUserTie />,
            to: "/admin/teacher-permission/list"
          },
          {
            label: "Teacher ID Card",
            icon: <FaUserTie />,
            to: "/admin/teacher/id-card"
          },
        ],
      },
      {
        title: "Subjects / Class",
        icon: <FaBook />,
        items: [
          { label: "Add Subject", icon: <FaBook />, to: "/admin/subject-add" },
          { label: "Assign Subject to Class", icon: <FaTasks />, to: "/admin/subject-class/assign" },
          { label: "Class & Section", icon: <GiNotebook />, to: "/admin/class-section/manage" },
          { label: "Class & Section Working Days", icon: <GiNotebook />, to: "/admin/class-section/working-days" },
        ],
      },

      // ✅ Attendance
      {
        title: "Attendance",
        icon: <FaClipboardList />,
        items: [
          {
            label: "Student Attendance",
            icon: <FaUserGraduate />,
            to: "/admin/attendance/student",
          },
          {
            label: "Teacher Attendance",
            icon: <FaChalkboardTeacher />,
            to: "/admin/attendance/teacher",
          },
        ],
      },
      {
        title: "Products & Inventory",
        icon: <FaBoxOpen />,
        items: [
          { label: "Add Category", icon: <FaLayerGroup />, to: "/admin/product-category" },
          { label: "Add Sub Category", icon: <FaBoxOpen />, to: "/admin/product-sub-category" },
          { label: "Stock Add", icon: <FaBoxOpen />, to: "/admin/product-bundle/create" },
          { label: "View Stock", icon: <FaListAlt />, to: "/admin/product-bundle/list" },
          { label: "View Vendor Stock", icon: <FaListAlt />, to: "/admin/product-vendor/list" },
        ],
      },

      {
        title: "Library",
        icon: <FaBook />,
        items: [
          {
            label: "Books",
            icon: <FaBookOpen />,
            to: "/admin/library/books",
          },
          {
            label: "Book Issue / Return",
            icon: <FaExchangeAlt />,
            to: "/admin/library/assign-books",
          },
        ],
      },

      {
        title: "Reports",
        icon: <TbReportSearch />,
        items: [
          {
            label: "Monthly Due",
            icon: <FaClipboardList />,
            to: "/admin/reports/monthly-due",
          },
          {
            label: "Profit & Loss",
            icon: <TbReportSearch />,
            to: "/admin/reports/profit-loss",
          },
          { label: "Financial Report", icon: <MdAccountBalance />, to: "/admin/reports/financial-report" }
          , { label: "Salary Report", icon: <FaMoneyBillWave />, to: "/admin/reports/salary" }
        ],
      },
      {
        title: "Expenses",
        icon: <Fa500Px />,
        items: [
          { label: "Add Expense", icon: <Fa500Px />, to: "/admin/expense/add" },
          { label: "Expense Categories", icon: <FaListAlt />, to: "/admin/expense/category" },
          { label: "Expense Sub Categories", icon: <FaLayerGroup />, to: "/admin/expense/sub-category" },
          {
            label: "Report",
            icon: <TbReportSearch />,
            to: "/admin/expense-report"
          },
          { label: "Vendors", icon: <FaUserTie />, to: "/admin/vendor/list" },
          { label: "Salary Structure", icon: <FaUserTie />, to: "/admin/salary-structure" },
          { label: "Salary Payroll", icon: <FaUserTie />, to: "/admin/salary-payroll" },
        ],
      },
      {
        title: "Applicants & Admissions",
        icon: <FaUsers />,
        items: [{ label: "View Applicants", icon: <FaUsers />, to: "/admin/applicant/view" },
        { label: "Publish Result", icon: <FaClipboardList />, to: "/admin/application/exam/publish_result" },
        ],
      },
      {
        title: "Certificates",
        icon: <FaRegFileAlt />,
        items: [
          {
            label: "School Leaving Certificate",
            icon: <FaRegFileAlt />,
            to: "/admin/certificate/school-leaving",
          },
          {
            label: "Character Certificate",
            icon: <FaRegFileAlt />,
            to: "/admin/certificate/character",
          },
          {
            label: "Bonafide Certificate",
            icon: <FaRegFileAlt />,
            to: "/admin/certificate/bonafide",
          },
        ],
      },
      {
        title: "Communication & Notices",
        icon: <FaBullhorn />,
        items: [
          { label: "Notices", icon: <FaBullhorn />, to: "/admin/notice" },
          { label: "Parent Interview", icon: <FaUserTie />, to: "/admin/application/parent/interview" },
        ],
      },
      {
        title: "School Management",
        icon: <FaUniversity />,
        items: [{ label: "Academic Years", icon: <FaUniversity />, to: "/admin/school/academic-years" }],
      },
      // {
      //   title: "Reports",
      //   icon: <FaClipboardList />,
      //   items: [
      //     { label: "Daily / Monthly Reports", icon: <FaClipboardList />, to: "/admin/reports" },
      //     { label: "Service / Commission Reports", icon: <FaRegFileAlt />, to: "/admin/reports/commission" },
      //   ],
      // },
      // {
      //   title: "Settings",
      //   icon: <FaCog />,
      //   items: [
      //     { label: "Profile / Settings", icon: <FaCog />, to: "/admin/settings" },
      //     { label: "Notifications", icon: <FaBell />, to: "/admin/notifications" },
      //   ],
      // },
    ],
    [billingRoute, roleIsSuper, walletRoute]
  );

  // Persist sidebar open state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem("sidebar_open", open ? "1" : "0");
    } catch (e) {
      // ignore
    }
  }, [open]);

  // Auto-open relevant menu if current pathname matches any child route
  useEffect(() => {
    const path = location.pathname || "";
    let found = "";
    for (const section of dropdownSections) {
      for (const it of section.items) {
        if (!it.to) continue;
        // exact match OR startsWith (so /admin/mark-sheet/list/123 also matches)
        if (path === it.to || path.startsWith(it.to)) {
          found = section.title;
          break;
        }
      }
      if (found) break;
    }
    setOpenMenu(found);
  }, [location.pathname, dropdownSections]);

  const toggleMenu = (key) => {
    setOpenMenu((prev) => (prev === key ? "" : key));
  };

  const handleNavigate = (to) => {
    if (!to) return;
    if (isMobile) setOpen(false);
    navigate(to);
  };

  // Helper to test active state for each child
  const isActive = (to) => {
    if (!to) return false;
    return location.pathname === to || location.pathname.startsWith(to);
  };

  return (
    <Box component="nav" sx={{ width: open ? DRAWER_WIDTH : MINI_WIDTH, flexShrink: 0 }}>
      <Drawer
        variant="persistent"
        open={open}
        sx={{
          "& .MuiDrawer-paper": {
            width: open ? DRAWER_WIDTH : MINI_WIDTH,
            transition: "all 0.3s ease-in-out",
            overflowX: "hidden",
            bgcolor: "#102a43",
            color: "white",
            borderRight: 0,
            background:
              "linear-gradient(180deg, #102a43 0%, #123a5a 48%, #155e75 100%)",
            boxShadow: "16px 0 42px rgba(16,42,67,0.18)",
          },
        }}
      >
        <Toolbar sx={{ display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
          {open ? (
            <Typography sx={{ fontWeight: 900, letterSpacing: 0, fontSize: 18 }}>
              Admin Panel
            </Typography>
          ) : (
            "AP"
          )}
        </Toolbar>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

        <List sx={{ p: 1 }}>
          {/* Dashboard */}
          <ListItemButton
            onClick={() => {
              handleNavigate("/admin/dashboard");
              setOpenMenu("");
            }}
            sx={{
              mb: 1,
              borderRadius: 1.5,
              py: 1.1,
              px: 1.25,
              bgcolor:
                location.pathname === "/admin/dashboard"
                  ? "rgba(125,211,252,0.18)"
                  : "transparent",
              boxShadow:
                location.pathname === "/admin/dashboard"
                  ? "inset 3px 0 0 rgba(125,211,252,0.95)"
                  : "none",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)", transform: "translateX(4px)" },
            }}
          >
            <ListItemIcon sx={{ minWidth: 44 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  display: "grid",
                  placeItems: "center",
                  bgcolor:
                    location.pathname === "/admin/dashboard"
                      ? "rgba(125,211,252,0.24)"
                      : "rgba(255,255,255,0.08)",
                  color: "white",
                  fontSize: 18,
                }}
              >
                <FaChartBar />
              </Box>
            </ListItemIcon>
            {open && (
              <ListItemText
                primary="Dashboard"
                primaryTypographyProps={{
                  sx: {
                    fontWeight: location.pathname === "/admin/dashboard" ? 800 : 600,
                    fontSize: 14,
                    color: "rgba(255,255,255,0.95)",
                  },
                }}
              />
            )}
          </ListItemButton>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 1 }} />

          {/* Dynamic Sections */}
          {dropdownSections.map((section) => (
            <Box key={section.title} sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => toggleMenu(section.title)}
                sx={{
                  borderRadius: 1.5,
                  mb: 0.3,
                  py: 1,
                  px: 1.25,
                  bgcolor: openMenu === section.title ? "rgba(125,211,252,0.16)" : "transparent",
                  boxShadow: openMenu === section.title ? "inset 3px 0 0 rgba(125,211,252,0.9)" : "none",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.1)", transform: "translateX(3px)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: "#dff7ff" }}>{section.icon}</ListItemIcon>

                {open && (
                  <>
                    <ListItemText
                      primary={section.title}
                      primaryTypographyProps={{
                        sx: { fontWeight: 700, fontSize: 15, color: "rgba(255,255,255,0.95)" },
                      }}
                    />
                    {openMenu === section.title ? <FaChevronDown /> : <FaChevronRight />}
                  </>
                )}
              </ListItemButton>

              <Collapse in={openMenu === section.title} timeout="auto" unmountOnExit>
                {section.items.map((m) => {
                  const active = isActive(m.to);
                  return (
                    <ListItemButton
                      key={m.label}
                      onClick={() => handleNavigate(m.to)}
                      sx={{
                        pl: 7,
                        py: 0.8,
                        mb: 0.3,
                        borderRadius: 1,
                        bgcolor: active ? "rgba(34,197,94,0.16)" : "transparent",
                        boxShadow: active ? "inset 3px 0 0 rgba(34,197,94,0.9)" : "none",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.1)", transform: "translateX(3px)" },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 30, color: active ? "#bbf7d0" : "#dff7ff" }}>{m.icon}</ListItemIcon>
                      {open && (
                        <ListItemText
                          primary={m.label}
                          primaryTypographyProps={{
                            sx: { fontWeight: active ? 700 : 500, fontSize: 14, color: "rgba(255,255,255,0.9)" },
                          }}
                        />
                      )}
                    </ListItemButton>
                  );
                })}
              </Collapse>
            </Box>
          ))}

          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mt: 2 }} />

          {/* Logout */}
          <ListItemButton
            onClick={() => {
              localStorage.clear();
              navigate("/login");
            }}
            sx={{
              borderRadius: 1.5,
              py: 1,
              px: 1.25,
              mt: 1,
              "&:hover": { bgcolor: "rgba(225,29,72,0.18)", transform: "translateX(4px)" },
            }}
          >
            <ListItemIcon sx={{ minWidth: 44 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "rgba(255,255,255,0.08)",
                  color: "white",
                }}
              >
                <FaSignOutAlt />
              </Box>
            </ListItemIcon>
            {open && (
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  sx: { fontWeight: 700, fontSize: 14, color: "rgba(255,255,255,0.95)" },
                }}
              />
            )}
          </ListItemButton>
        </List>
      </Drawer>
    </Box>
  );
}
